import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {ErrorMessage, Field, Form, Formik} from "formik";
import * as Yup from "yup";
import {clearMessage} from "../slices/message";
import {messages} from "../i18n/messages";
import {LOCALES} from "../i18n/locales";
import {resetUserPassword} from "../services/admin.service";
import YupPassword from "yup-password";
import PasswordField from "./PasswordField";

const ResetUserPassword = () => {
  const [successful, setSuccessful] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { message } = useSelector((state) => state.message);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  const initialValues = {
    email: "",
    newPassword: "",
    matchingPassword: "",
  };

  YupPassword(Yup);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(messages[LOCALES.ENGLISH].invalid_email)
      .required(messages[LOCALES.ENGLISH].field_required),
    newPassword: Yup.string().password()
        .min(8, messages[LOCALES.ENGLISH].password_length)
        .max(30, messages[LOCALES.ENGLISH].password_length)
        .minUppercase(1, messages[LOCALES.ENGLISH].password_upper_case)
        .minNumbers(1, messages[LOCALES.ENGLISH].password_number)
        .minSymbols(1, messages[LOCALES.ENGLISH].password_symbol)
        .required(messages[LOCALES.ENGLISH].field_required),
    matchingPassword: Yup.string()
        .min(8, messages[LOCALES.ENGLISH].password_length)
        .max(30, messages[LOCALES.ENGLISH].password_length)
        .minUppercase(1, messages[LOCALES.ENGLISH].password_upper_case)
        .minNumbers(1, messages[LOCALES.ENGLISH].password_number)
        .minSymbols(1, messages[LOCALES.ENGLISH].password_symbol)
        .oneOf([Yup.ref("newPassword"), null], messages[LOCALES.ENGLISH].password_match)
        .required(messages[LOCALES.ENGLISH].field_required),
  });

  const handleReset = (formValue) => {
    const { email } = formValue;
    const { newPassword } = formValue;
    setSuccessful(false);
    setIsLoading(true);

    dispatch(resetUserPassword({ email, newPassword }))
      .unwrap()
      .then(() => {
        setSuccessful(true);
        setIsLoading(false);
      })
      .catch(() => {
        setSuccessful(false);
        setIsLoading(false);
      });
  };

  return (
    <div className="container-fluid d-flex flex-column justify-content-center align-items-center py-5" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
      <div className="card border-0 shadow-lg" style={{ borderRadius: '24px', width: '100%', maxWidth: '450px', overflow: 'hidden' }}>
        <div className="card-body p-4 p-md-5">
          <div className="text-center mb-4">
            <div className="d-inline-flex align-items-center justify-content-center bg-danger bg-opacity-10 rounded-circle mb-3" style={{ width: '64px', height: '64px' }}>
              <i className="bi bi-shield-lock text-danger fs-2"></i>
            </div>
            <h2 className="fw-bold mb-0" style={{ color: 'var(--text-dark)' }}>Reset User Password</h2>
          </div>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleReset}
        >
          <Form>
            {!successful && (
                <div className="d-flex flex-column gap-3">
                  <div>
                    <label htmlFor="email" className="text-muted small fw-bold mb-1">Email</label>
                    <Field name="email" type="email" className="form-control form-control-lg bg-light border-0" style={{ fontSize: '1rem' }} />
                    <ErrorMessage name="email" component="div" className="text-danger small mt-1 fw-medium" />
                  </div>
                  <div>
                    <label htmlFor="newPassword" className="text-muted small fw-bold mb-1">Password</label>
                    <PasswordField name="newPassword" />
                    <ErrorMessage name="newPassword" component="div" className="text-danger small mt-1 fw-medium" />
                  </div>
                  <div>
                    <label htmlFor="matchingPassword" className="text-muted small fw-bold mb-1">Repeat Password</label>
                    <PasswordField name="matchingPassword" />
                    <ErrorMessage name="matchingPassword" component="div" className="text-danger small mt-1 fw-medium" />
                  </div>

                  <button type="submit" className="btn-premium w-100 rounded-pill py-2 mt-3" disabled={isLoading}>
                    {isLoading ? (
                        <span className="spinner-border spinner-border-sm me-2"></span>
                    ) : (
                        <i className="bi bi-arrow-clockwise me-2"></i>
                    )}
                    <span className="fw-bold">Reset Password</span>
                  </button>
                </div>
            )}
          </Form>
        </Formik>
        </div>
      </div>

      {message && (
        <div className="mt-4 w-100" style={{ maxWidth: '450px' }}>
          <div
            className={`alert ${successful ? "alert-success" : "alert-danger"} border-0 shadow-sm rounded-3 d-flex align-items-center`}
            role="alert"
          >
            <i className={`bi ${successful ? "bi-check-circle-fill" : "bi-exclamation-triangle-fill"} me-2 fs-5`}></i>
            <div>{message}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResetUserPassword;
